// Thin wrapper around the auth service's registration endpoint.
//
// The frontend and the reverse proxy always share one origin (see
// services/proxy), so a relative path is all we need here - no base URL
// or CORS configuration required. The proxy forwards everything under
// /api/ to the auth service unmodified.
const API_BASE = '/api';

export class RegisterError extends Error {
  constructor(message, { code, status } = {}) {
    super(message);
    this.name = 'RegisterError';
    this.code = code;
    this.status = status;
  }
}

/**
 * Calls POST /api/register.
 *
 * Resolves with `{ userId, username }` on success (HTTP 201).
 * Rejects with a RegisterError on any known failure (400/409) or
 * unexpected/network error, with a human-readable `message`.
 */
export async function registerUser(username, password) {
  let response;
  try {
    response = await fetch(`${API_BASE}/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
    });
  } catch (err) {
    throw new RegisterError(
      'Could not reach the server. Check your connection and try again.',
      { code: 'network_error' },
    );
  }

  let body = null;
  try {
    body = await response.json();
  } catch (err) {
    // Non-JSON response body; fall through with body === null.
  }

  if (!response.ok) {
    const message = body?.error_description
      ?? 'Something went wrong while creating your account. Please try again.';
    throw new RegisterError(message, { code: body?.error, status: response.status });
  }

  return { userId: body?.user_id, username: body?.username };
}
